/**
 * Page-builder workspace tabs (WS-1) — the main content area is *context-routed*:
 * the page's UI canvas plus any opened Query / JS / Stato editors live as
 * PERSISTENT TABS across the top of the main pane (the Appsmith model), instead
 * of being crammed into the bottom dock. This module is the pure tab-state core
 * — the tab union and the open / activate / close / prune transitions — so the
 * page can be unit-tested without React.
 *
 * Scope is the CURRENT page only (queries/js/state are page-scoped, ADR-0044);
 * switching page resets the set to just `ui` — the caller does that on a
 * `pageId` change. Queries are keyed by `id`, JS Objects by `name` (they have no
 * id, see `PageJsObject`).
 */

export type WorkspaceTab =
  // Each entry is an AREA — a singleton top-level tab. `query` / `js` are
  // master/detail AREAS (a list + the selected editor), not one tab per
  // query/object (the owner's UX pass: tabs are top-level, the list is the
  // area's own sidebar). The SELECTED query/object lives in component state.
  | { kind: "ui" }
  | { kind: "query" }
  | { kind: "js" }
  | { kind: "state" }
  // WS-4b — APP-GLOBAL contexts opened from the rail's BOTTOM zone (position =
  // scope). `globalState` edits the app-wide `$global` declared bag (survives
  // navigation); `settings` is the inline portal/branding config (the old
  // header gear). Both are singletons that open in the main pane like `state`.
  | { kind: "globalState" }
  | { kind: "settings" }
  // ADR-0046 — app-global libraries (rail bottom zone): external JS libraries
  // (`apps.js_libraries`) and the reusable custom-component library. Singletons.
  | { kind: "jsLibraries" }
  | { kind: "customComponents" }
  // ADR-0047 — app-level Query manager (rail bottom zone): the app's SERVER
  // primitives (`app_queries`) — invocable / scheduled / REST-exposed. Singleton.
  | { kind: "appQueries" }
  // ADR-0048 single-IDE — app data TABLES manager (rail bottom zone): the app's
  // data-table schema editor, migrated off the app-detail page. Singleton.
  | { kind: "appTables" }
  // ADR-0048 single-IDE — app DATASETS manager (rail bottom zone): the app's
  // read-model projection editor (master/detail), migrated off app-detail.
  | { kind: "appDatasets" }
  // ADR-0048 single-IDE — app FORMS manager (rail bottom zone): the app's form
  // builder (master/detail), migrated off app-detail.
  | { kind: "appForms" }
  // ADR-0059 — external catalog resources the app CONSUMES (Core areas):
  // linked Datasets + Knowledge bases, scoped per-role in the access matrix.
  | { kind: "appLinkedDatasets" }
  | { kind: "appLinkedKbs" }
  // ADR-0060 S5 — tenant-global Report Types the app CONSUMES (Core "Report").
  | { kind: "appLinkedReports" }
  // ADR-0048 single-IDE — Members & roles + Interfaces + Scheduled rules +
  // Automations, migrated off the app-detail page into the editor
  // (self-contained, single-pane). Singletons.
  | { kind: "appMembers" }
  // ADR-0049 Slice B — chat and voce are SEPARATE flat interfaces (independent
  // brains), so each gets its OWN tab — not one shared "Interfacce" tab.
  | { kind: "appInterfaceChat" }
  | { kind: "appInterfaceVoice" }
  // ADR-0051 — REST (the single exposure registry): two rail areas — the endpoints
  // editor (master/detail) + the OpenAPI documentation.
  | { kind: "appRest" }
  | { kind: "appRestDocs" }
  // ADR-0058 — GraphQL (the SDL-derived twin of REST): the resources editor
  // (master/detail) + the GraphiQL playground (test + live introspection docs).
  | { kind: "appGraphql" }
  | { kind: "appGraphqlDocs" }
  | { kind: "appScheduledRules" }
  | { kind: "appAutomations" };

/** Stable key identifying a tab (React key + active-tab tracking). `ui`/`state`/
 *  `globalState`/`settings`/`jsLibraries`/`customComponents` are singletons;
 *  query is keyed by id, js by name. */
export function tabKey(t: WorkspaceTab): string {
  switch (t.kind) {
    case "ui":
      return "ui";
    case "state":
      return "state";
    case "globalState":
      return "globalState";
    case "settings":
      return "settings";
    case "jsLibraries":
      return "jsLibraries";
    case "customComponents":
      return "customComponents";
    case "appQueries":
      return "appQueries";
    case "appTables":
      return "appTables";
    case "appDatasets":
      return "appDatasets";
    case "appForms":
      return "appForms";
    case "appLinkedDatasets":
      return "appLinkedDatasets";
    case "appLinkedKbs":
      return "appLinkedKbs";
    case "appLinkedReports":
      return "appLinkedReports";
    case "appMembers":
      return "appMembers";
    case "appInterfaceChat":
      return "appInterfaceChat";
    case "appInterfaceVoice":
      return "appInterfaceVoice";
    case "appRest":
      return "appRest";
    case "appRestDocs":
      return "appRestDocs";
    case "appGraphql":
      return "appGraphql";
    case "appGraphqlDocs":
      return "appGraphqlDocs";
    case "appScheduledRules":
      return "appScheduledRules";
    case "appAutomations":
      return "appAutomations";
    case "query":
      return "query";
    case "js":
      return "js";
  }
}

/** The always-present home tab — the UI canvas; never closable, always first. */
export const UI_TAB: WorkspaceTab = { kind: "ui" };

/** Open `t` if not already present (else no-op), returning the next list. */
export function openTab(tabs: WorkspaceTab[], t: WorkspaceTab): WorkspaceTab[] {
  const key = tabKey(t);
  if (tabs.some((x) => tabKey(x) === key)) return tabs;
  return [...tabs, t];
}

/** Close the tab with `key` — the `ui` tab is never closable. */
export function closeTab(tabs: WorkspaceTab[], key: string): WorkspaceTab[] {
  if (key === "ui") return tabs;
  return tabs.filter((x) => tabKey(x) !== key);
}

/** Which tab to activate after closing `closingKey` while `activeKey` is active:
 *  the previous neighbour (Appsmith-like), falling back to `ui`. Closing a
 *  BACKGROUND tab leaves the active one untouched. */
export function nextActiveKey(tabs: WorkspaceTab[], closingKey: string, activeKey: string): string {
  if (activeKey !== closingKey) return activeKey;
  const idx = tabs.findIndex((x) => tabKey(x) === closingKey);
  const remaining = tabs.filter((x) => tabKey(x) !== closingKey);
  if (remaining.length === 0) return "ui";
  const prev = remaining[Math.max(0, idx - 1)];
  return tabKey(prev ?? remaining[0]);
}

/** A page's remembered workspace (WS-5): its open tabs + the active one. */
export interface TabSnapshot {
  openTabs: WorkspaceTab[];
  activeKey: string;
}

/** The default workspace for a page never visited before — just the UI tab. */
export function defaultTabs(): TabSnapshot {
  return { openTabs: [UI_TAB], activeKey: "ui" };
}

/** Recall a page's remembered workspace (WS-5), or the default when it has never
 *  been opened. The caller saves the OUTGOING page's snapshot before switching,
 *  so flipping between pages restores each one's open editors instead of always
 *  resetting to the UI tab. */
export function recallTabs(mem: Map<string, TabSnapshot>, pageId: string): TabSnapshot {
  return mem.get(pageId) ?? defaultTabs();
}

/** Close the `query` / `js` AREA tabs when the page has no queries / JS Objects
 *  left (so the bar doesn't offer an empty area). `ui`/`state`/the app-global
 *  tabs always survive. Returns the SAME array reference when nothing changed
 *  (so a caller's setState is a no-op). */
export function pruneTabs(
  tabs: WorkspaceTab[],
  hasQueries: boolean,
  hasJs: boolean,
): WorkspaceTab[] {
  const next = tabs.filter((t) => {
    if (t.kind === "query") return hasQueries;
    if (t.kind === "js") return hasJs;
    return true;
  });
  return next.length === tabs.length ? tabs : next;
}
